
import React from 'react';
import { Share2, Image, Trash2, Loader } from 'lucide-react';
import { shareOnMobile } from "react-mobile-share";

const MyMemories = ({ 
  photos, 
  navigateTo, 
  setUserPhotos, 
  deleteMemoryAndSave, 
  isLoadingMemories 
}) => {
  
  // Funzione per eliminare un ricordo
  const handleDeleteMemory = async (memoryId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo ricordo? Questa azione non può essere annullata.')) {
      const success = await deleteMemoryAndSave(memoryId);
      if (!success) {
        alert('Errore nell\'eliminare il ricordo. Riprova più tardi.');
      }
    }
  };

  // Funzione per il download delle immagini
  const downloadImage = (imageUrl, fileName = 'celtic-druid-memory.jpg') => {
    // Se l'immagine è già un Data URL
    if (imageUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    
    // Se è un URL normale, dobbiamo prima recuperare l'immagine
    fetch(imageUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Errore durante il download dell\'immagine:', error);
        alert('Non è stato possibile scaricare l\'immagine. Riprova più tardi.');
      });
  };

  // Funzione per condividere con react-mobile-share
  const shareImage = (memory) => {
    try {
      shareOnMobile({
        text: `Guarda questo confronto tra ${memory.historicalPhoto.year} e oggi! 🍀`,
        url: "https://tinyurl.com/celticdruidstoria",
        title: `${memory.historicalPhoto.title} - Prima e Dopo`,
        images: [memory.compositeImage],
      });
    } catch (error) {
      console.error('Errore nella condivisione:', error);
      // Fallback: copia il testo negli appunti
      if (navigator.clipboard) {
        navigator.clipboard.writeText(`Guarda questo confronto tra ${memory.historicalPhoto.year} e oggi! 🍀 - ${memory.historicalPhoto.title}`);
        alert('Testo copiato negli appunti!');
      }
    }
  };

  // Mostra loader durante il caricamento iniziale
  if (isLoadingMemories) {
    return (
      <div className="container mx-auto px-4 py-6 text-amber-100">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-amber-500 mb-4" />
          <p className="text-center text-amber-200">Caricamento ricordi...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 text-amber-100">
      <h2 className="text-xl font-bold mb-4 text-center text-amber-300">
        I Miei Ricordi {photos.length > 0 && `(${photos.length})`}
      </h2>
      
      {photos.length === 0 ? (
        <div className="bg-stone-800 p-6 rounded-lg text-center">
          <Image size={40} className="mx-auto mb-4 text-stone-600" />
          <h3 className="text-lg font-bold mb-2">Nessun ricordo salvato</h3>
          <p className="text-sm mb-4 text-amber-200">
            Inizia a creare ricordi comparando le foto storiche con quelle attuali
          </p>
          <button 
            onClick={() => navigateTo('gallery')}
            className="py-2 px-4 bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            Esplora la galleria
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {photos.map(memory => (
            <div key={memory.id} className="bg-stone-800 rounded-lg overflow-hidden shadow-md">
              <img 
                src={memory.compositeImage} 
                alt={`Ricordo: ${memory.historicalPhoto.title}`}
                className="w-full object-cover" 
                loading="lazy"
              />
              
              <div className="p-4">
                <h3 className="font-bold text-amber-300 mb-1">
                  {memory.historicalPhoto.title} - Prima e Dopo
                </h3>
                <p className="text-sm text-amber-100 mb-1">
                  Confronto tra {memory.historicalPhoto.year} e 2025
                </p>
                <p className="text-xs text-amber-200 mb-3">
                  Creato il {new Date(memory.createdAt).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                
                <div className="flex justify-between items-center">
                  <button 
                    onClick={() => downloadImage(
                      memory.compositeImage, 
                      `celtic-druid-${memory.historicalPhoto.title.replace(/\s+/g, '-').toLowerCase()}-${new Date(memory.createdAt).getFullYear()}.jpg`
                    )}
                    className="flex items-center justify-center gap-2 py-2 px-3 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm transition-colors"
                    title="Scarica l'immagine"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Salva
                  </button>
                  
                  <div className="flex gap-2">
                    <button 
                      onClick={() => shareImage(memory)} 
                      className="flex items-center justify-center gap-2 py-2 px-3 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm transition-colors"
                      title="Condividi il ricordo"
                    >
                      <Share2 size={16} />
                      Condividi
                    </button>
                    
                    <button 
                      onClick={() => handleDeleteMemory(memory.id)} 
                      className="flex items-center justify-center gap-1 py-2 px-3 bg-red-700 hover:bg-red-600 rounded-lg text-sm transition-colors"
                      title="Elimina ricordo"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Footer con statistiche */}
          {photos.length > 0 && (
            <div className="text-center text-xs text-amber-300 mt-6 p-3 bg-stone-800 rounded-lg">
              💾 I tuoi ricordi sono salvati localmente sul dispositivo
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyMemories;